// Opportunity sales stage in Flow (CEO 2026-10-15, "Opportunity → Quote").
//
// Open → Awaiting Approval (a quote is sent) → Closed Won (a quote becomes an
// invoice) move on their own. The team only marks an opportunity LOST — with a
// reason from the list — or reopens a lost one. After 21 days without a stage
// change, Open / Awaiting opportunities show an alert: Review · Snooze 7 days ·
// Mark lost. Expected value is typed by the rep — never a sum of quotes.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";

export type SalesStage = "open" | "awaiting_approval" | "closed_won" | "closed_lost";

export interface OpportunitySales {
  stage: SalesStage;
  stageLabel: string;
  stageChangedAt: string;
  stageAgeDays: number;
  lossReason: string | null;
  lossReasonLabel: string | null;
  lossNote: string | null;
  expectedValue: string | null;
  expectedCloseDate: string | null;
  snoozedUntil: string | null;
  stale: boolean;
  liveOrder?: boolean;
}

export const LOSS_REASON_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "price", label: "Price" },
  { value: "timing", label: "Timing" },
  { value: "other_supplier", label: "Chose another supplier" },
  { value: "no_response", label: "No response" },
  { value: "budget_cut", label: "Budget cut" },
  { value: "other", label: "Other" },
];

const STAGE_TONE: Record<SalesStage, "info" | "warn" | "active" | "closed"> = {
  open: "info",
  awaiting_approval: "warn",
  closed_won: "active",
  closed_lost: "closed",
};

export function SalesStagePill({ sales }: { sales: Pick<OpportunitySales, "stage" | "stageLabel"> }) {
  return (
    <StatusPill tone={STAGE_TONE[sales.stage]} className="text-[12px] normal-case">
      {sales.stageLabel}
    </StatusPill>
  );
}

function money(v: string | number | null): string {
  if (v == null || v === "") return "Not set";
  const n = Number(v);
  if (!Number.isFinite(n)) return "Not set";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function shortDate(iso: string | null): string {
  if (!iso) return "Not set";
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const reason = (err.body as { reason?: string } | undefined)?.reason;
    if (reason) return reason;
  }
  return fallback;
}

/** The 21-day alert copy, shared by the opportunity page and the dashboard. */
export function staleAlertText(stageLabel: string, days: number): string {
  return `This opportunity has been in ${stageLabel} for ${days} days.`;
}

/**
 * The opportunity page's sales strip: stage, its age, expected value + close
 * date (editable), the 21-day alert, Mark lost / Reopen.
 */
export function OpportunitySalesBar(props: {
  projectId: string;
  sales: OpportunitySales;
  /** Where "Review opportunity" goes — the quotes list. */
  reviewHref?: string;
  invalidateKeys: unknown[][];
}) {
  const { projectId, sales } = props;
  const qc = useQueryClient();
  const [lostOpen, setLostOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(sales.expectedValue ?? "");
  const [closeDate, setCloseDate] = useState(sales.expectedCloseDate ?? "");
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    for (const k of props.invalidateKeys) void qc.invalidateQueries({ queryKey: k });
    void qc.invalidateQueries({ queryKey: ["sales"] });
  };

  const snooze = useMutation({
    mutationFn: () => api(`/projects/${projectId}/snooze`, { method: "POST", body: "{}" }),
    onSuccess: refresh,
    onError: (e) => setError(errorText(e, "Couldn't snooze — try again.")),
  });
  const reopen = useMutation({
    mutationFn: () => api(`/projects/${projectId}/sales-stage`, { method: "POST", body: JSON.stringify({ action: "reopen" }) }),
    onSuccess: refresh,
    onError: (e) => setError(errorText(e, "Couldn't reopen — try again.")),
  });
  const forecast = useMutation({
    mutationFn: () =>
      api(`/projects/${projectId}/forecast`, {
        method: "PUT",
        body: JSON.stringify({
          expected_value: value.trim() === "" ? null : value.trim().replace(/[$,]/g, ""),
          expected_close_date: closeDate === "" ? null : closeDate,
        }),
      }),
    onSuccess: () => {
      setEditing(false);
      refresh();
    },
    onError: (e) => setError(errorText(e, "Couldn't save — check the amount and date.")),
  });

  const active = sales.stage === "open" || sales.stage === "awaiting_approval";

  return (
    <section className="mb-6 rounded-lg border border-rule bg-white" aria-label="Opportunity stage">
      {sales.stale && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule bg-warn-bg px-5 py-3 text-[15px] text-ink" role="alert">
          <span className="flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4 shrink-0 text-warn" aria-hidden />
            {staleAlertText(sales.stageLabel, sales.stageAgeDays)}
          </span>
          <span className="flex flex-wrap items-center gap-2">
            {props.reviewHref && (
              <Button asChild variant="outline" size="sm">
                <Link to={props.reviewHref}>Review opportunity</Link>
              </Button>
            )}
            <Button variant="outline" size="sm" disabled={snooze.isPending} onClick={() => snooze.mutate()}>
              {snooze.isPending ? "Snoozing…" : "Snooze 7 days"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setLostOpen(true)}>
              Mark lost
            </Button>
          </span>
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-4 px-5 py-4">
        <dl className="flex flex-wrap gap-x-10 gap-y-3">
          <div>
            <dt className="text-xs text-muted">Stage</dt>
            <dd className="mt-1 flex items-center gap-2">
              <SalesStagePill sales={sales} />
              <span className="text-sm text-muted">
                {sales.stageAgeDays === 0 ? "since today" : `for ${sales.stageAgeDays} ${sales.stageAgeDays === 1 ? "day" : "days"}`}
                {active && sales.liveOrder ? " · quote approved — won at invoice" : ""}
              </span>
            </dd>
          </div>
          {sales.stage === "closed_lost" && sales.lossReasonLabel && (
            <div>
              <dt className="text-xs text-muted">Lost because</dt>
              <dd className="mt-1 text-[15px] font-medium text-ink">
                {sales.lossReasonLabel}
                {sales.lossNote ? <span className="font-normal text-muted"> — {sales.lossNote}</span> : null}
              </dd>
            </div>
          )}
          {editing ? (
            <>
              <label className="block">
                <span className="text-xs text-muted">Expected value</span>
                <input
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  inputMode="decimal"
                  placeholder="e.g. 12000"
                  className="mt-1 block h-9 w-36 rounded-md border border-rule px-2 text-[15px]"
                />
              </label>
              <label className="block">
                <span className="text-xs text-muted">Expected close (optional)</span>
                <input
                  type="date"
                  value={closeDate}
                  onChange={(e) => setCloseDate(e.target.value)}
                  className="mt-1 block h-9 rounded-md border border-rule px-2 text-[15px]"
                />
              </label>
            </>
          ) : (
            <>
              <div>
                <dt className="text-xs text-muted">Expected value</dt>
                <dd className="mt-1 text-[15px] font-medium text-ink">{money(sales.expectedValue)}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted">Expected close</dt>
                <dd className="mt-1 text-[15px] font-medium text-ink">{shortDate(sales.expectedCloseDate)}</dd>
              </div>
            </>
          )}
        </dl>
        <div className="flex flex-wrap items-center gap-2">
          {editing ? (
            <>
              <Button size="sm" disabled={forecast.isPending} onClick={() => forecast.mutate()}>
                {forecast.isPending ? "Saving…" : "Save"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setValue(sales.expectedValue ?? "");
                setCloseDate(sales.expectedCloseDate ?? "");
                setEditing(true);
              }}
            >
              Edit value &amp; date
            </Button>
          )}
          {active && !sales.stale && (
            <Button size="sm" variant="outline" onClick={() => setLostOpen(true)}>
              Mark lost
            </Button>
          )}
          {sales.stage === "closed_lost" && (
            <Button size="sm" variant="outline" disabled={reopen.isPending} onClick={() => reopen.mutate()}>
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              {reopen.isPending ? "Reopening…" : "Reopen"}
            </Button>
          )}
        </div>
      </div>
      {error && <p className="border-t border-rule px-5 py-2 text-sm text-risk">{error}</p>}
      {lostOpen && (
        <MarkLostDialog
          projectId={projectId}
          onClose={() => setLostOpen(false)}
          onDone={() => {
            setLostOpen(false);
            refresh();
          }}
        />
      )}
    </section>
  );
}

export function MarkLostDialog(props: { projectId: string; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api(`/projects/${props.projectId}/sales-stage`, {
        method: "POST",
        body: JSON.stringify({ action: "mark_lost", reason, note: note.trim() || null }),
      }),
    onSuccess: props.onDone,
    onError: (e) => setError(errorText(e, "Couldn't save — try again.")),
  });
  const needsNote = reason === "other";
  const firstRadio = useRef<HTMLInputElement>(null);
  // Focus the first reason once; Escape closes (unless saving).
  const closeRef = useRef(props.onClose);
  closeRef.current = props.onClose;
  const pendingRef = useRef(false);
  pendingRef.current = save.isPending;
  useEffect(() => {
    firstRadio.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !pendingRef.current) closeRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const ready = reason !== "" && (!needsNote || note.trim().length > 0);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mark-lost-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <h2 id="mark-lost-title" className="text-xl font-semibold text-ink">Mark this opportunity lost?</h2>
        <p className="mt-1 text-[15px] text-muted">Pick the main reason. You can reopen it later.</p>
        <fieldset className="mt-4 space-y-2">
          <legend className="sr-only">Loss reason</legend>
          {LOSS_REASON_OPTIONS.map((o, i) => (
            <label key={o.value} className="flex items-center gap-2.5 text-[15px] text-ink">
              <input
                ref={i === 0 ? firstRadio : undefined}
                type="radio"
                name="loss-reason"
                value={o.value}
                checked={reason === o.value}
                onChange={() => setReason(o.value)}
                className="h-4 w-4 accent-ink"
              />
              {o.label}
            </label>
          ))}
        </fieldset>
        <label className="mt-4 block">
          <span className="text-sm text-muted">{needsNote ? "What happened? (required)" : "Note (optional)"}</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            rows={3}
            className="mt-1 block w-full rounded-md border border-rule px-3 py-2 text-[15px]"
          />
        </label>
        {error && <p className="mt-3 text-sm text-risk">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={props.onClose}>
            Cancel
          </Button>
          <Button disabled={!ready || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Mark lost"}
          </Button>
        </div>
      </div>
    </div>
  );
}
